import React from 'react';
import PDFMerger from 'pdf-merger-js/browser';
import PropTypes from 'prop-types';
import Joi from 'joi';

import Progress from '../themed/progress';
import Form from '../common/form';

import { EXCEL_APPLICATION_TYPE, IMAGE_LIMITS, MAX_PDF_UPLOAD_SIZE, PDF_APPLICATION_TYPE } from '../../utils/constants';
import { createReport, updateReport } from '../../services/reportService';
import { getCurrentUser } from '../../services/authService';
import { base64Upload } from "../../services/fileUpload";
import { uploadSingleFileToS3Server } from '../../services/aws';
import { AxiosResponse } from 'axios';

import withUsers from '../hoc/withUsers';

import * as toastService from '../../services/toastService';
import * as utils from '../../utils/utilities';
import * as models from '../../utils/models';

const UPLOAD_FILE_TYPES = [PDF_APPLICATION_TYPE];
const MAX_FILES = 1;

class UploadReportPDF extends Form {
	state = {
		data:  { pdf: [], iso: [], approvedComment: '' },
		errors: {},
		loading: false,
		progress: 0,
		step: 1,
		division: {},
	};

	schema = {
		pdf: Joi.array().min(1).max(MAX_FILES).required().label('Report PDF'),
		iso: Joi.array().min(0).max(IMAGE_LIMITS.EXCEL_ISO_UPLOAD).required().label('Report PDF'),
		approvedComment: Joi.string().min(3).max(25).required().label('Comment'),
	};

	static propTypes = {
		project: PropTypes.object,
		onUploadReport: PropTypes.func,
		coverUrl: PropTypes.string,
	};

	getApiData = async () => {
		const project: models.Project = this.props.project;
		const user = getCurrentUser() as models.User;

		const pdfUrl = await this.handlePDFMerge();
		const excelDocumentUrl = this.state.data.iso.length > 0 ? (this.state.data.iso[0] as models.PDFImageProps).src : undefined;

		const apiData: any = { 
			projectId: project._id,
			reportUrl: pdfUrl, 
			savedProgressUrl: pdfUrl,
			excelDocumentUrl,
			riskRating: null,
			approvedBy: user.id,
			approvedByName: `${user.firstName} ${user.lastName}`,
			approvedBySignature: user.signature,
			approvedByDesignation: user.designation,
			approvedComment: this.state.data.approvedComment,
		};

		const supervisor: models.User = this.props.users.find((user: models.User) => user.id === project.supervisorId);
		if (supervisor) {
			apiData.createdBySignature = supervisor.id;
		}

		const contactPerson: models.User = this.props.users.find((user: models.User) => user.id === project.contactPersonId);
		if (contactPerson) {
			apiData.acceptedBy = contactPerson.id;
			apiData.acceptedByName = `${contactPerson.firstName} ${contactPerson.lastName}`;
			apiData.acceptedBySignature = contactPerson.signature;
			apiData.acceptedByDesignation = contactPerson.designation;
			apiData.acceptedComment = '';
		}

		return apiData;
	};

	handlePDFMerge = async () => {
		if (this.props.coverUrl) {
			const merger = new PDFMerger();
			const pdfUrls = this.state.data.pdf.map((file: any) => file.src).concat([this.props.coverUrl]);

			for (const url of pdfUrls) {
				await merger.add(url);
			}

			const buffer = await merger.saveAsBuffer();
			const bytes = new Uint8Array(buffer);
			let binary = '';
			
			for (let i = 0; i < bytes.byteLength; i++) {
				binary += String.fromCharCode(bytes[i]);
			}

			return base64Upload(this.props.project.documentNumber + ' merged.pdf', btoa(binary));
		}

		return (this.state.data.pdf[0] as models.PDFImageProps).src;
	};
	
	onDrop = async (files: File[]) => {
		const errors: any = { ...this.state.errors };
		const data: any = { ...this.state.data };

		const isValidType: boolean = utils.validateFileType(files, UPLOAD_FILE_TYPES);
		const isValidSize: boolean = utils.validateFileSize(files, MAX_PDF_UPLOAD_SIZE);
		const isValidLength: boolean = utils.validateFileLength(files, MAX_FILES);

		if (isValidLength && isValidSize && isValidLength) {
			this.setState({ loading: true });

			const result = await uploadSingleFileToS3Server(files[0]);
			data.pdf = [result];
			delete errors.pdf;

			this.setState({ loading: false });
		} else {
			if (!isValidType) errors.pdf = 'File type is not supported!';
			if (!isValidSize) errors.pdf = utils.getFileSizeValidationMessage(MAX_PDF_UPLOAD_SIZE);
			if (!isValidLength) errors.pdf = 'Only ' + MAX_FILES + ' allowed!';
		}

		this.setState({ data, errors });
	};

	postData = async (): Promise<void> => {
		let apiData = await this.getApiData();
		let project: models.Project = this.props.project;
		let response: AxiosResponse<any, any>;

		if (project.report && project.report._id) {
			response = await updateReport(project.report._id, apiData, this.handleProgressChange)
		} else {
			response = await createReport(apiData, this.handleProgressChange);
		}
		
		this.props.onUploadReport(response.data);
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: `Saving PDF`,
			success: () => `PDF Uploaded`,
			error: utils.getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form
					autoComplete="off"
					onSubmit={this.handleSubmit}
					className="p-3 container"
				>
					<h1 className="form-title">Upload Report PDF</h1>

					<div className="bg-light p-4 border">
						{this.renderFileInput({
							max: MAX_FILES,
							multiple: MAX_FILES > 1,
							name: 'pdf',
							accept: 'application/pdf',
							label: 'Report PDF',
							sublabel: 'Upload PDF File',
							types: UPLOAD_FILE_TYPES,
							onDrop: this.onDrop
						})}
					</div>

					<div className="divider my-4"></div>

					<div className="bg-light p-4 border">
						{this.renderFileInput({
							max: IMAGE_LIMITS.EXCEL_ISO_UPLOAD,
							multiple: IMAGE_LIMITS.EXCEL_ISO_UPLOAD > 1,
							name: 'iso',
							accept: EXCEL_APPLICATION_TYPE,
							label: 'Anomaly location or ISO or Drawing (Excel File)',
							sublabel: 'Upload Anomaly location or ISO or Drawing',
							types: [EXCEL_APPLICATION_TYPE],
						})}
					</div>

					<div className="divider my-4"></div>

					{this.renderTextArea({
						name: 'approvedComment',
						label: 'Comment',
						placeholder: 'Enter Comment',
						rows: 4,
					})}

					<div className="d-flex justify-content-end mt-3">
						{this.renderButton(this.state.loading ? 'Uploading' : 'Submit')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withUsers(UploadReportPDF);
